<template>
  <div class="bar-chart">
    <ul class="labels">
      <li v-for="item in datasets" :key="item.label">
        <div class="color" :style="{ 'background-color': item.color }" />
        <div class="text">{{ $t(item.label) }}</div>
      </li>
    </ul>
    <div class="bar-chart-wrapper" ref="wrapperRef" :style="{ width: `${w}px`, height: `${h}px` }">
      <canvas ref="chartRef" />
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api';
import $ from 'jquery';

import Chart from '@/modules/chart';
import COLORS from '@/consts/colors';

export default {
  name: 'BarChart',
  props: {
    datasets: {
      type: Array,
      required: true
    },
    labels: {
      type: Array,
      default: () => []
    },
    width: {
      type: [String, Number],
      default: 0
    },
    height: {
      type: [String, Number],
      default: 0
    }
  },
  components: {
  },
  setup(props, { root }) {
    const wrapperRef = ref(null);
    const chartRef = ref(null);
    const chart = ref(null);

    const datasets = computed(() => props.datasets);

    const w = computed(() => {
      let ww = parseFloat(props.width);
      if (!ww) {
        ww = $(wrapperRef.value).width();
      }

      return ww;
    });

    const h = computed(() => {
      let hh = parseFloat(props.height);
      if (!hh) {
        hh = w.value;
      }

      return hh;
    });

    watch([datasets, chart], ([sets, newChart]) => {
      if (!newChart) return;
      const c = newChart;

      const yMax = Math.max(...sets.reduce((prev, i) => {
        const next = [...prev];
        i.data.forEach((j, k) => {
          next[k] = (next[k] || 0) + j;
        });

        return next;
      }, []));

      const series = sets.reduce((prev, i) => {
        const next = [...prev];
        i.data.forEach((j, x) => {
          next[x] = {
            x,
            y: [
              ...(next[x]?.y || []),
              j
            ]
          };
        });

        return next;
      }, []);

      c.series = series;
      c.labels = props.labels.map((i) => root.$t(i));
      c.colors = sets.map((i) => i.color);
      c.yMin = 0;
      c.yMax = yMax;
      c.xMin = 0;
      c.xMax = series.length;
      c.stacked = true;
      c.xStep = 1;
      c.yStep = Math.ceil(yMax / 6 / 10) * 10;

      c.clear();
      c.drawAxises(false, false, false, true);
      c.drawStackedBars();
    }, {
      immediate: true
    });

    watch([w, h, chartRef], ([ww, hh, newChartRef]) => {
      if (!newChartRef) return;

      const c = new Chart(newChartRef, ww * 2, hh * 2);
      c.textColor = COLORS.INDEPENDENCE;
      c.gridColor = COLORS.ALTO;
      c.fontSize = 26;
      c.fontFace = 'Source Sans Pro';

      chart.value = c;
    }, {
      immediate: true
    });

    return {
      wrapperRef,
      chartRef,
      w,
      h
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

.bar-chart {
  position: relative;

  .labels {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: space-around;
    margin-bottom: 20px;

    li {
      .color {
        height: 10px;
        width: 30px;
        border-radius: 20px;
        display: inline-block;
        vertical-align: middle;
        margin-right: 7px;
      }

      .text {
        display: inline-block;
        font-size: 13px;
        vertical-align: middle;
      }
    }
  }

  canvas {
    position: relative;
    top: 0;
    left: 0;
    transform: scale(.5);
    transform-origin: top left;
  }
}
</style>
